import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Save, User as UserIcon, Mail, Phone, Calendar, Shield, Copy, Eye, EyeOff } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { userService } from '@/services/userService';
import { locationService } from '@/services/locationService';
import { User, CreateUserRequest, UpdateUserRequest } from '@/types/user';
import { Button, Input } from '@/components/ui';
import { useNotification } from '@/hooks/useNotification';

const userSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  middle_name: z.string().optional().transform(val => val === '' ? null : val),
  last_name: z.string().optional().transform(val => val === '' ? null : val),
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
  phone_number: z.string().min(10, 'Phone number must be at least 10 digits'),
  employee_id: z.string().min(1, 'Employee ID is required'),
  gender: z.enum(['Male', 'Female'], { required_error: 'Gender is required' }),
  permanent_address: z.string().min(1, 'Permanent address is required'),
  district: z.string().optional(),
  joining_date: z.string().min(1, 'Joining date is required'),
  roles: z.array(z.string()).min(1, 'At least one role is required'),
  status: z.enum(['Active', 'Inactive']).default('Active'),
});

type UserFormData = z.infer<typeof userSchema>;

interface UserFormProps {
  user?: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * Optional prefill applied when opening the form in create mode (i.e.
   * `user` is null). Lets callers like "Create user from candidate" seed
   * known fields (name, email, phone, address) without forking the form.
   * Ignored when editing an existing user.
   */
  prefillData?: Partial<UserFormData>;
}

interface PasswordDisplayProps {
  password: string;
  userName: string;
  email: string;
  dbId?: number;
  onClose: () => void;
}

interface ValidationErrorDisplayProps {
  error: any;
  onClose: () => void;
  onTryAgain: () => void;
}

export const UserForm: React.FC<UserFormProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess,
  prefillData,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState<{password: string; userName: string; email: string; dbId?: number} | null>(null);
  const [showValidationError, setShowValidationError] = useState<any>(null);
  const { showError, showSuccess } = useNotification();
  const isEditing = !!user;

  // Get available roles and districts
  const { data: rolesData } = useQuery({
    queryKey: ['application-roles'],
    queryFn: () => userService.getApplicationRoles(),
  });

  const { data: districts, isLoading: districtsLoading, error: districtsError } = useQuery({
    queryKey: ['districts'],
    queryFn: () => locationService.getDistrictOptions(),
  });


  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      status: 'Active',
      roles: [],
    },
  });

  // Populate form when editing or clear when creating new
  useEffect(() => {
    if (user && isOpen) {
      reset({
        first_name: user.first_name,
        middle_name: user.middle_name || '',
        last_name: user.last_name || '',
        email: user.email,
        phone_number: user.phone_number,
        employee_id: user.employee_id,
        gender: user.gender,
        permanent_address: user.permanent_address || '',
        district: user.district || '',
        joining_date: user.joining_date ? new Date(user.joining_date).toISOString().split('T')[0] : '',
        roles: user.roles || [],
        status: user.status as 'Active' | 'Inactive',
      });
      setSelectedRoles(user.roles || []);
      setSelectedDistricts(user.district ? user.district.split(',') : []);
    } else if (!user && isOpen) {
      // Empty defaults, then overlay any prefill the caller provided.
      // Falsy values in `prefillData` are ignored so a partial prefill
      // doesn't accidentally clear a field a smarter caller already set.
      const base = {
        first_name: '',
        middle_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        employee_id: '',
        gender: undefined,
        permanent_address: '',
        district: '',
        joining_date: '',
        status: 'Active' as const,
        roles: [] as string[],
      };
      const merged = { ...base } as Record<string, unknown>;
      if (prefillData) {
        for (const [k, v] of Object.entries(prefillData)) {
          if (v !== undefined && v !== null && v !== '') merged[k] = v;
        }
      }
      reset(merged as UserFormData);
      const prefilledRoles = (merged.roles as string[] | undefined) ?? [];
      const prefilledDistrict = (merged.district as string | undefined) ?? '';
      setSelectedRoles(prefilledRoles);
      setSelectedDistricts(prefilledDistrict ? prefilledDistrict.split(',').filter(Boolean) : []);
    }
    
    // Clear password state when form opens (but not immediately after setting it)
    if (isOpen && !showPassword) {
      setShowPassword(null);
      setShowValidationError(null);
    }
    // `prefillData` is intentionally not in deps: we only seed at open time
    // so changes to it while the modal is open don't clobber user edits.
    // Callers (e.g. CreateUserBanner) should wait until prefill data is
    // ready before flipping `isOpen` to true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isOpen, reset]);

  const handleRoleChange = (role: string, checked: boolean) => {
    let newRoles: string[];
    if (checked) {
      newRoles = [...selectedRoles, role];
    } else {
      newRoles = selectedRoles.filter(r => r !== role);
    }
    setSelectedRoles(newRoles);
    setValue('roles', newRoles);
  };

  const handleDistrictChange = (districtId: string, isChecked: boolean) => {
    let newDistricts: string[];
    if (isChecked) {
      newDistricts = [...selectedDistricts, districtId];
    } else {
      newDistricts = selectedDistricts.filter(id => id !== districtId);
    }
    setSelectedDistricts(newDistricts);
    setValue('district', newDistricts.join(','), { shouldValidate: true });
  };

  const onSubmit = async (data: UserFormData) => {
    setIsSubmitting(true);
    try {
      if (isEditing && user) {
        const updateData: UpdateUserRequest = {
          db_id: user.db_id,
          fusion_auth_user_id: user.fusion_auth_user_id,
          first_name: data.first_name,
          middle_name: data.middle_name,
          last_name: data.last_name,
          email: data.email,
          phone_number: data.phone_number,
          employee_id: data.employee_id,
          gender: data.gender,
          permanent_address: data.permanent_address,
          district: data.district,
          joining_date: data.joining_date,
          roles: data.roles,
          status: data.status,
        };
        const response = await userService.updateUser(updateData);
        showSuccess('User Updated', response.message || `${data.first_name}${data.last_name ? ' ' + data.last_name : ''} has been updated successfully.`);
        onSuccess();
        onClose();
      } else {
        const createData: CreateUserRequest = {
          first_name: data.first_name,
          middle_name: data.middle_name,
          last_name: data.last_name,
          email: data.email,
          phone_number: data.phone_number,
          employee_id: data.employee_id,
          gender: data.gender,
          permanent_address: data.permanent_address,
          district: data.district,
          joining_date: data.joining_date,
          roles: data.roles,
        };
        
        const response = await userService.createUser(createData);
        
        // Check if response contains password data
        if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
          const createdUser = response.data[0];
          
          if (createdUser && createdUser.success && createdUser.password) {
            // Show password dialog
            setShowPassword({
              password: createdUser.password,
              userName: `${data.first_name}${data.last_name ? ' ' + data.last_name : ''}`,
              email: createdUser.email || data.email,
              dbId: createdUser.db_id
            });
            // Don't call onSuccess() yet - wait for password dialog to close
            return;
          }
        }
        
        showSuccess('User Created', `${data.first_name}${data.last_name ? ' ' + data.last_name : ''} has been created successfully.`);
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      // Handle validation errors with detailed display
      if (error.isValidationError) {
        setShowValidationError(error.validationData);
        return;
      }
      
      // Handle other errors
      showError(
        isEditing ? 'Update Failed' : 'Creation Failed',
        error.message || `Failed to ${isEditing ? 'update' : 'create'} user`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Password Display Component
  const PasswordDisplay: React.FC<PasswordDisplayProps> = ({ password, userName, email, dbId, onClose }) => {
    const [showPasswordText, setShowPasswordText] = useState(false);
    const [copied, setCopied] = useState(false);

    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(password);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy password to clipboard');
      }
    };

    const handleClose = () => {
      // Clear all dialog states
      setShowPassword(null);
      setShowValidationError(null);
      // Now trigger success callback and close form
      onSuccess();
      onClose();
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-rgf-gray-900">User Created Successfully</h3>
            <Button
              variant="ghost"
              size="sm"
              icon={<X className="h-5 w-5" />}
              onClick={handleClose}
            />
          </div>
          
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <div className="flex-shrink-0">
                  <span className="text-green-600 text-lg">✅</span>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-green-800">User Created Successfully!</h4>
                </div>
              </div>
              <div className="space-y-1 text-sm text-green-700">
                <p><span className="font-medium">Name:</span> {userName}</p>
                <p><span className="font-medium">Email:</span> {email}</p>
                {dbId && <p><span className="font-medium">User ID:</span> {dbId}</p>}
              </div>
            </div>
            
            <p className="text-sm text-rgf-gray-600">
              Please copy the generated password and share it securely with the user. This password will only be shown once.
            </p>
            
            <div className="bg-rgf-gray-50 rounded-lg p-4 border">
              <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                Generated Password
              </label>
              <div className="flex items-center space-x-2">
                <div className="flex-1 flex items-center bg-white border border-rgf-gray-300 rounded-lg px-3 py-2">
                  <input
                    type={showPasswordText ? 'text' : 'password'}
                    value={password}
                    readOnly
                    className="flex-1 bg-transparent border-0 outline-none text-sm font-mono"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={showPasswordText ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    onClick={() => setShowPasswordText(!showPasswordText)}
                  />
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Copy className="h-4 w-4" />}
                  onClick={copyToClipboard}
                  disabled={copied}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>Important:</strong> This password will only be shown once. Please copy it and share it securely with the user.
              </p>
            </div>
          </div>
          
          <div className="flex justify-end mt-6">
            <Button onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Validation Error Display Component
  const ValidationErrorDisplay: React.FC<ValidationErrorDisplayProps> = ({ error, onClose, onTryAgain: _onTryAgain }) => {
    const handleClose = () => {
      setShowValidationError(null);
      onClose();
    };

    const handleTryAgain = () => {
      setShowValidationError(null);
      // Don't close the form, let user fix the issues
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-red-600">Validation Error</h3>
            <Button
              variant="ghost"
              size="sm"
              icon={<X className="h-5 w-5" />}
              onClick={handleClose}
            />
          </div>
          
          <div className="space-y-4">
            <p className="text-sm text-rgf-gray-600">
              {error.message || 'User creation failed due to validation errors.'}
            </p>
            
            {error.error && error.error.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-rgf-gray-700">Specific Issues:</h4>
                {error.error.map((validationError: any, index: number) => (
                  <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="space-y-2">
                      {validationError.email_id && (
                        <p className="text-sm">
                          <span className="font-medium text-red-800">Email:</span>
                          <span className="text-red-700 ml-1">{validationError.email_id}</span>
                        </p>
                      )}
                      {validationError.phone_number && (
                        <p className="text-sm">
                          <span className="font-medium text-red-800">Phone:</span>
                          <span className="text-red-700 ml-1">{validationError.phone_number}</span>
                        </p>
                      )}
                      <p className="text-sm text-red-800 font-medium">
                        {validationError.error}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              variant="secondary"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleTryAgain}
            >
              Fix & Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Show validation error dialog if available
  if (showValidationError) {
    return (
      <ValidationErrorDisplay
        error={showValidationError}
        onClose={onClose}
        onTryAgain={() => {}}
      />
    );
  }

  // Show password dialog if available
  if (showPassword) {
    return (
      <PasswordDisplay
        password={showPassword.password}
        userName={showPassword.userName}
        email={showPassword.email}
        dbId={showPassword.dbId}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-rgf-gray-200">
          <div className="flex items-center">
            <UserIcon className="h-6 w-6 text-rgf-primary-600 mr-3" />
            <h2 className="text-xl font-semibold text-rgf-gray-900">
              {isEditing ? 'Edit User' : 'Create New User'}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            icon={<X className="h-5 w-5" />}
            onClick={onClose}
          />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-medium text-rgf-gray-900 mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                {...register('first_name')}
                label="First Name"
                placeholder="Enter first name"
                error={errors.first_name?.message}
                required
              />
              <Input
                {...register('middle_name')}
                label="Middle Name"
                placeholder="Enter middle name (optional)"
                error={errors.middle_name?.message}
              />
              <Input
                {...register('last_name')}
                label="Last Name"
                placeholder="Enter last name (optional)"
                error={errors.last_name?.message}
              />
              <Input
                {...register('employee_id')}
                label="Employee ID"
                placeholder="Enter employee ID"
                error={errors.employee_id?.message}
                required
              />
              <div>
                <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                  Gender *
                </label>
                <select
                  {...register('gender')}
                  className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
                {errors.gender && (
                  <p className="mt-1 text-sm text-red-600">{errors.gender.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-medium text-rgf-gray-900 mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                {...register('email')}
                type="email"
                label="Email Address"
                placeholder="Enter email address"
                startIcon={<Mail className="h-4 w-4" />}
                error={errors.email?.message}
                required
              />
              <Input
                {...register('phone_number')}
                type="tel"
                label="Phone Number"
                placeholder="Enter phone number"
                startIcon={<Phone className="h-4 w-4" />}
                error={errors.phone_number?.message}
                required
              />
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                  Permanent Address *
                </label>
                <textarea
                  {...register('permanent_address')}
                  rows={3}
                  className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  placeholder="Enter permanent address"
                />
                {errors.permanent_address && (
                  <p className="mt-1 text-sm text-red-600">{errors.permanent_address.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Work Information */}
          <div>
            <h3 className="text-lg font-medium text-rgf-gray-900 mb-4">Work Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-rgf-gray-700 mb-3">
                  Districts (Optional)
                </label>
                {districtsLoading ? (
                  <p className="text-sm text-rgf-gray-500">Loading districts...</p>
                ) : districtsError ? (
                  <p className="text-sm text-red-600">Error loading districts</p>
                ) : (
                  <div className="space-y-2 max-h-32 overflow-y-auto border border-rgf-gray-300 rounded-lg p-3">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedDistricts.includes('*')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDistricts(['*']);
                            setValue('district', '*');
                          } else {
                            setSelectedDistricts([]);
                            setValue('district', '');
                          }
                        }}
                        className="rounded border-rgf-gray-300 text-rgf-primary-600 focus:ring-rgf-primary-500"
                      />
                      <span className="text-sm font-medium text-rgf-gray-700">All Districts (*)</span>
                    </label>
                    {districts?.map((district) => (
                      <label key={district.value} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedDistricts.includes(district.value) && !selectedDistricts.includes('*')}
                          disabled={selectedDistricts.includes('*')}
                          onChange={(e) => handleDistrictChange(district.value, e.target.checked)}
                          className="rounded border-rgf-gray-300 text-rgf-primary-600 focus:ring-rgf-primary-500 disabled:opacity-50"
                        />
                        <span className={`text-sm ${selectedDistricts.includes('*') ? 'text-rgf-gray-400' : 'text-rgf-gray-700'}`}>
                          {district.label}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
                {errors.district && (
                  <p className="mt-1 text-sm text-red-600">{errors.district.message}</p>
                )}
              </div>
              <Input
                {...register('joining_date')}
                type="date"
                label="Joining Date"
                startIcon={<Calendar className="h-4 w-4" />}
                error={errors.joining_date?.message}
                required
              />
              {isEditing && (
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Status *
                  </label>
                  <select
                    {...register('status')}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Roles */}
          <div>
            <h3 className="text-lg font-medium text-rgf-gray-900 mb-4 flex items-center">
              <Shield className="h-5 w-5 mr-2" />
              Roles & Permissions
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {rolesData?.roles?.map((role: string | { id: string; title: string; description?: string }) => {
                const roleId = typeof role === 'string' ? role : role.id;
                const roleTitle = typeof role === 'string' ? role : role.title;
                
                return (
                  <label key={roleId} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(roleId)}
                      onChange={(e) => handleRoleChange(roleId, e.target.checked)}
                      className="rounded border-rgf-gray-300 text-rgf-primary-600 focus:ring-rgf-primary-500"
                    />
                    <span className="text-sm text-rgf-gray-700">{roleTitle}</span>
                  </label>
                );
              })}
            </div>
            {errors.roles && (
              <p className="mt-1 text-sm text-red-600">{errors.roles.message}</p>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-rgf-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
              icon={<Save className="h-4 w-4" />}
            >
              {isSubmitting 
                ? (isEditing ? 'Updating...' : 'Creating...')
                : (isEditing ? 'Update User' : 'Create User')
              }
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};